import { useState, useEffect, useRef, useCallback, useMemo, memo, type ElementType } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Building2, ChevronDown, ChevronUp, Copy, Eye, History, ImagePlus, Laptop, MessageSquare, Monitor, PackagePlus, Pencil, RefreshCw, SendHorizonal, Square, Trash2, Upload, UserCog, Users, Wrench, X } from 'lucide-react'
import {
  Button,
  ChatBubble,
  ConfirmDialog,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  QuoteSidebar,
  StatusBadge,
  useToast,
} from '@/components/ui'
import { useTenantStore } from '@/stores/tenants.ts'
import { useAuthStore } from '@/stores/auth.ts'
import {
  fetchQuote,
  fetchQuoteMessages,
  streamQuoteMessage,
  fetchQuotePositions,
  exportQuoteToSap,
  updateQuote,
  copyQuote,
  deleteQuote,
  fetchContactPersons,
  reopenQuote,
  QuoteExportValidationError,
  type Quote,
  type QuoteMessage,
  type QuotePosition,
  type ExportValidationError,
  type SapContactPerson,
  type SapExportHistoryEntry,
} from '@/api/quotes.ts'
import { ResolveMissingItemsModal } from './ResolveMissingItemsModal.tsx'
import { DocumentDetailModal } from '@/components/documents/DocumentDetailModal.tsx'
import { hasHtmlTags } from '@/lib/safeHtml.ts'
import { type CachedDocument, type DocType } from '@/api/sapDocuments.ts'
import { SapArticleCreationWizard } from './SapArticleCreationWizard.tsx'
import { QuoteStepDebugModal } from './QuoteStepDebugModal.tsx'
import { ChangeCustomerModal, type CustomerSelection } from './ChangeCustomerModal.tsx'
import { SapTransferWizardModal } from './SapTransferWizardModal.tsx'

const SCENARIO_LABELS: Record<string, string> = {
  laptop: 'Client-Hardware',
  teams: 'Teams-Migration',
  meetingRoom: 'Besprechungsraum',
  free: 'Freier Dialog',
}

const SCENARIO_ICONS: Record<string, ElementType> = {
  laptop: Laptop,
  teams: Users,
  meetingRoom: Monitor,
  free: MessageSquare,
}

function ScenarioBadge({ scenario }: { scenario: string }) {
  const Icon = SCENARIO_ICONS[scenario] ?? MessageSquare
  const label = SCENARIO_LABELS[scenario] ?? scenario
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-caption font-medium text-gray-600">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  )
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

// ─── Tool-Group: collapse consecutive tool messages into one row ──────────────

type ToolGroup = { messages: QuoteMessage[] }

function groupToolMessages(msgs: QuoteMessage[]): Array<QuoteMessage | ToolGroup> {
  const result: Array<QuoteMessage | ToolGroup> = []
  let i = 0
  while (i < msgs.length) {
    if (msgs[i].role === 'tool') {
      const group: QuoteMessage[] = []
      while (i < msgs.length && msgs[i].role === 'tool') {
        group.push(msgs[i])
        i++
      }
      result.push({ messages: group })
    } else {
      result.push(msgs[i])
      i++
    }
  }
  return result
}

function ToolGroupRow({ group }: { group: ToolGroup }) {
  const [open, setOpen] = useState(false)
  const names = group.messages.map((m) => m.toolName ?? 'tool')
  // summarise: "add_position ×8, search_sap_articles ×2"
  const counts = names.reduce<Record<string, number>>((acc, n) => {
    acc[n] = (acc[n] ?? 0) + 1
    return acc
  }, {})
  const summary = Object.entries(counts)
    .map(([n, c]) => (c > 1 ? `${n} ×${c}` : n))
    .join(', ')

  return (
    <div className="flex gap-3 w-full">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center">
        <Wrench className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 w-full text-left bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 hover:bg-amber-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        >
          <span className="text-caption font-semibold text-amber-800 font-mono truncate flex-1">{summary}</span>
          {open
            ? <ChevronUp className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            : <ChevronDown className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />}
        </button>
        {open && (
          <div className="mt-1 space-y-1">
            {group.messages.map((msg) => (
              <ChatBubble
                key={msg.id}
                role="tool"
                content={msg.content ?? ''}
                toolName={msg.toolName ?? undefined}
                toolInput={msg.toolInputJson ?? undefined}
                toolOutput={msg.toolOutputJson ?? undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


/**
 * Shows the SAP mandant this quote belongs to (from quote.tenantId, not the
 * globally active mandant). When the stored mandant differs from the globally
 * active one a warning indicator is shown so the user knows the context.
 * Clicking the badge still lets the user switch the global active mandant
 * (needed for other pages), but the quote itself is always exported to its
 * own stored mandant regardless.
 */
function ActiveTenantBadge({ quoteTenantId }: { quoteTenantId?: string }) {
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const tenants = useTenantStore((s) => s.tenants)
  const setActiveTenant = useTenantStore((s) => s.setActiveTenant)
  const [open, setOpen] = useState(false)

  // Prefer the quote's own mandant; fall back to the globally active one
  // while the quote is still loading.
  const quoteTenant = quoteTenantId ? tenants.find((t) => t.id === quoteTenantId) : null
  const activeTenant = tenants.find((t) => t.id === activeTenantId)
  const displayTenant = quoteTenant ?? activeTenant
  const mismatch = !!(quoteTenantId && activeTenantId && quoteTenantId !== activeTenantId)

  if (!displayTenant) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-caption font-medium text-amber-900"
        title="Kein Mandant aktiv"
      >
        <Building2 className="w-3.5 h-3.5" />
        kein Mandant
      </span>
    )
  }

  const label = `SAP: ${displayTenant.code}${displayTenant.name ? ` · ${displayTenant.name}` : ''}`
  const tooltipText = mismatch
    ? `Angebot-Mandant: ${displayTenant.code} — Aktiver Mandant: ${activeTenant?.code ?? '?'} — Export immer zum Angebot-Mandanten`
    : `SAP-Mandant dieses Angebots — Klick zum Wechseln`

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <DropdownTrigger asChild>
        <button
          type="button"
          className={[
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-caption font-medium',
            mismatch
              ? 'bg-amber-100 text-amber-900 hover:bg-amber-200'
              : 'bg-brand-50 text-brand-900 hover:bg-brand-100',
          ].join(' ')}
          title={tooltipText}
        >
          <Building2 className="w-3.5 h-3.5" />
          {label}
          {mismatch && <AlertTriangle className="w-3.5 h-3.5" />}
        </button>
      </DropdownTrigger>
      <DropdownContent align="start">
        {mismatch && (
          <div className="px-3 py-2 text-caption text-amber-800 border-b border-amber-100 max-w-xs">
            Angebot-Mandant: <span className="font-mono">{displayTenant.code}</span> — Export geht immer dorthin, unabhängig vom aktiven Mandanten.
          </div>
        )}
        {tenants.map((t) => (
          <DropdownItem key={t.id} onSelect={() => setActiveTenant(t.id)}>
            <span className="font-mono">{t.code}</span>
            {t.name ? <span className="text-caption text-gray-500 ml-2">{t.name}</span> : null}
            {t.id === activeTenantId ? <span className="ml-auto text-emerald-600">●</span> : null}
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  )
}

// ─── Quote header panel (document-level fields) ───────────────────────────────

interface QuoteHeaderPanelProps {
  quote: Quote
  locked: boolean
  onUpdate: (patch: Parameters<typeof updateQuote>[1]) => Promise<void>
}

const QuoteHeaderPanel = memo(function QuoteHeaderPanel({ quote, locked, onUpdate }: QuoteHeaderPanelProps) {
  const [ticketDraft, setTicketDraft] = useState(quote.ticketId ?? '')
  const [commentsDraft, setCommentsDraft] = useState(quote.comments ?? '')
  const [saving, setSaving] = useState(false)
  const [contactPersons, setContactPersons] = useState<SapContactPerson[]>([])
  const [loadingContacts, setLoadingContacts] = useState(false)
  // Default to preview when content contains HTML tags, edit mode for plain text
  const [commentsPreview, setCommentsPreview] = useState(() => hasHtmlTags(quote.comments ?? ''))

  // Sync drafts when quote changes externally
  useEffect(() => { setTicketDraft(quote.ticketId ?? '') }, [quote.ticketId])
  useEffect(() => {
    setCommentsDraft(quote.comments ?? '')
    setCommentsPreview(hasHtmlTags(quote.comments ?? ''))
  }, [quote.comments])

  // Load contact persons when customer changes
  useEffect(() => {
    if (!quote.customerSapBpCode) { setContactPersons([]); return }
    setLoadingContacts(true)
    fetchContactPersons(quote.customerSapBpCode)
      .then(setContactPersons)
      .catch(() => setContactPersons([]))
      .finally(() => setLoadingContacts(false))
  }, [quote.customerSapBpCode])

  async function save(patch: Parameters<typeof updateQuote>[1]) {
    if (locked) return
    setSaving(true)
    try { await onUpdate(patch) } finally { setSaving(false) }
  }

  const selectedContact = contactPersons.find((c) => String(c.CntctCode) === quote.contactPersonCode)

  return (
    <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3 space-y-2 bg-gray-50/60">
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Belegkopf</p>

      {/* Ticket-Nr */}
      <div>
        <label className="block text-[10px] text-gray-500 mb-0.5">Ticket-Nr (U_OST_TS_TicketID)</label>
        <input
          type="text"
          value={ticketDraft}
          disabled={locked}
          onChange={(e) => setTicketDraft(e.target.value)}
          onBlur={() => {
            const val = ticketDraft.trim() || null
            if (val !== (quote.ticketId ?? null)) save({ ticketId: val })
          }}
          placeholder="z.B. 12345"
          className="w-full rounded border border-gray-200 px-2 py-1 text-small focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400"
        />
      </div>

      {/* Bemerkungen */}
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <label className="block text-[10px] text-gray-500">Bemerkungen (Comments)</label>
          {!locked && (
            <button
              type="button"
              onClick={() => {
                if (commentsPreview) {
                  setCommentsPreview(false)
                } else {
                  const val = commentsDraft.trim() || null
                  if (val !== (quote.comments ?? null)) save({ comments: val })
                  setCommentsPreview(true)
                }
              }}
              className="inline-flex items-center gap-0.5 text-[10px] text-brand-600 hover:text-brand-700"
              title={commentsPreview ? 'Bearbeiten' : 'Vorschau'}
            >
              {commentsPreview
                ? <><Pencil className="w-3 h-3" /> Bearbeiten</>
                : <><Eye className="w-3 h-3" /> Vorschau</>}
            </button>
          )}
        </div>
        {(!commentsPreview && !locked) ? (
          <textarea
            value={commentsDraft}
            rows={2}
            onChange={(e) => setCommentsDraft(e.target.value)}
            onBlur={() => {
              const val = commentsDraft.trim() || null
              if (val !== (quote.comments ?? null)) save({ comments: val })
            }}
            placeholder="Freitext für SAP-Angebotskopf"
            className="w-full rounded border border-gray-200 px-2 py-1 text-small focus:outline-none focus:ring-1 focus:ring-brand-400 resize-none"
          />
        ) : (
          <div
            className="w-full min-h-[3rem] rounded border border-gray-100 bg-gray-50 px-2 py-1 text-small"
          >
            {commentsDraft
              ? <span className="whitespace-pre-wrap break-words">{commentsDraft}</span>
              : <span className="text-gray-400 italic">Freitext für SAP-Angebotskopf</span>}
          </div>
        )}
      </div>

      {/* Ansprechpartner */}
      <div>
        <label className="block text-[10px] text-gray-500 mb-0.5">
          Ansprechpartner
          {loadingContacts && <span className="ml-1 text-gray-400 italic">(laden…)</span>}
        </label>
        {!quote.customerSapBpCode ? (
          <p className="text-[10px] text-gray-400 italic">Erst Kunde mit SAP-BP verknüpfen</p>
        ) : (
          <select
            value={quote.contactPersonCode ?? ''}
            disabled={locked || loadingContacts}
            onChange={(e) => {
              const code = e.currentTarget.value || null
              const person = contactPersons.find((c) => String(c.CntctCode) === code)
              save({ contactPersonCode: code, contactPersonName: person?.Name ?? null })
            }}
            className="w-full rounded border border-gray-200 px-1.5 py-1 text-small bg-white focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="">— kein Ansprechpartner —</option>
            {contactPersons.map((c) => (
              <option key={c.CntctCode} value={String(c.CntctCode)}>
                {c.Name}
                {c.Position ? ` · ${c.Position}` : ''}
              </option>
            ))}
          </select>
        )}
        {selectedContact?.E_Mail && (
          <p className="text-[10px] text-gray-400 mt-0.5 truncate">{selectedContact.E_Mail}</p>
        )}
      </div>

      {saving && <p className="text-[10px] text-gray-400 italic">Speichern…</p>}
    </div>
  )
})

export interface AngebotDialogProps {
  /** Quote-ID. Wenn nicht gesetzt, wird useParams() verwendet (Routing-Modus). */
  quoteId?: string
  /**
   * Embedded-Modus: laesst PageHeader, Breadcrumbs und „Zurueck"-Button weg
   * und verzichtet auf Vollbildhoehe. Wird im DocumentDetailModal genutzt,
   * damit der Dialog nicht zwischen Page und Modal hin/her springt.
   */
  embedded?: boolean
  /** Wird im embedded-Modus aufgerufen, wenn der User „Schliessen" drueckt. */
  onClose?: () => void
  /** Called after a successful deletion so the parent can refresh its list. */
  onDeleted?: () => void
}

export function AngebotDialogPage(props: AngebotDialogProps = {}) {
  const params = useParams<{ quoteId: string }>()
  const quoteId = props.quoteId ?? params.quoteId
  const embedded = !!props.embedded
  const navigate = useNavigate()

  const tenants = useTenantStore((s) => s.tenants)
  const currentUser = useAuthStore((s) => s.user)
  const profilePhotoUrl = useAuthStore((s) => s.profilePhotoUrl)
  const fetchProfilePhoto = useAuthStore((s) => s.fetchProfilePhoto)

  const [quote, setQuote] = useState<Quote | null>(null)
  const [messages, setMessages] = useState<QuoteMessage[]>([])
  const [positions, setPositions] = useState<QuotePosition[]>([])

  const [loadingQuote, setLoadingQuote] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(true)
  const [loadingPositions, setLoadingPositions] = useState(true)

  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [messagesError, setMessagesError] = useState<string | null>(null)
  const [positionsError, setPositionsError] = useState<string | null>(null)

  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  /** Live aufgebauter Text-Buffer fuer die gerade streamende Assistant-Bubble. */
  const [streamingText, setStreamingText] = useState<string>('')
  /** Live-Streaming-Tools (Tool-Use-Block-Streaming): pro Index halten wir
   *  Tool-Name + bisher akkumuliertes Input-JSON. Wird gerendert als
   *  amber Bubble mit „… wird ausgefuehrt"-Indikator. */
  const [streamingTools, setStreamingTools] = useState<Array<{ index: number; toolName: string; accumulatedJson: string }>>([])
  /** AbortController fuer den aktuellen Stream — wird vom Stop-Button getriggert. */
  const abortRef = useRef<AbortController | null>(null)
  /**
   * Wenn der User Enter drueckt, waehrend noch ein Stream laeuft, wird die
   * neue Message hier zwischengepuffert. Der laufende Stream wird abgebrochen,
   * der finally-Block startet die naechste Message automatisch.
   */
  const pendingSendRef = useRef<string | null>(null)

  const [showExportConfirm, setShowExportConfirm] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportErrors, setExportErrors] = useState<ExportValidationError[] | null>(null)
  const [exportErrorMessage, setExportErrorMessage] = useState<string | null>(null)
  const [showResolveItems, setShowResolveItems] = useState(false)
  const [resolveTargetIds, setResolveTargetIds] = useState<string[] | undefined>(undefined)
  const [resolveHints, setResolveHints] = useState<string[] | undefined>(undefined)
  const [showArticleWizard, setShowArticleWizard] = useState(false)
  const [showSapPreview, setShowSapPreview] = useState(false)
  const [showQuoteStepDebug, setShowQuoteStepDebug] = useState(false)
  const [showTransferWizard, setShowTransferWizard] = useState(false)

  const [showChangeCustomer, setShowChangeCustomer] = useState(false)
  const [changeCustomerLoading, setChangeCustomerLoading] = useState(false)
  const [changeCustomerError, setChangeCustomerError] = useState<string | null>(null)
  const [copying, setCopying] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showReopenConfirm, setShowReopenConfirm] = useState(false)
  const [reopening, setReopening] = useState(false)

  const { toast } = useToast()

  const [attachedImages, setAttachedImages] = useState<Array<{ mediaType: string; data: string; preview: string }>>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  /** true wenn der User aktuell am unteren Ende der Liste ist (mit kleiner
   *  Toleranz). Wird in der Banner-Logik verwendet. */
  const [atBottom, setAtBottom] = useState(true)
  /** Zaehler neuer Nachrichten, die seit dem letzten Scroll-To-Bottom
   *  reingekommen sind, waehrend der User oben war. Banner zeigt den Wert. */
  const [unseenCount, setUnseenCount] = useState(0)

  const scrollToBottom = useCallback((opts?: { force?: boolean }) => {
    const behavior = opts?.force ? 'auto' : 'smooth'
    messagesEndRef.current?.scrollIntoView({ behavior })
    setUnseenCount(0)
  }, [])

  function handleScroll() {
    const el = messagesScrollRef.current
    if (!el) return
    // Toleranz: alles <40px vom Boden zaehlt als „unten"
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const isAtBottom = distanceFromBottom < 40
    setAtBottom(isAtBottom)
    if (isAtBottom) setUnseenCount(0)
  }

  const refetchPositions = useCallback(() => {
    if (!quoteId) return
    fetchQuotePositions(quoteId)
      .then(setPositions)
      .catch((err) => setPositionsError(err instanceof Error ? err.message : String(err)))
  }, [quoteId])

  const refetchQuote = useCallback(() => {
    if (!quoteId) return
    fetchQuote(quoteId)
      .then(setQuote)
      .catch(() => {})
  }, [quoteId])

  const handleQuoteHeaderUpdate = useCallback(
    async (patch: Parameters<typeof updateQuote>[1]) => {
      const updated = await updateQuote(quoteId!, patch)
      setQuote(updated)
    },
    [quoteId],
  )

  const groupedMessages = useMemo(() => groupToolMessages(messages), [messages])

  useEffect(() => {
    if (profilePhotoUrl === undefined) fetchProfilePhoto()
  }, [profilePhotoUrl, fetchProfilePhoto])

  useEffect(() => {
    if (!quoteId) return
    setLoadingQuote(true)
    fetchQuote(quoteId)
      .then(setQuote)
      .catch((err) => setQuoteError(err instanceof Error ? err.message : 'Fehler beim Laden'))
      .finally(() => setLoadingQuote(false))
  }, [quoteId])

  useEffect(() => {
    if (!quoteId) return
    setLoadingMessages(true)
    fetchQuoteMessages(quoteId)
      .then(setMessages)
      .catch((err) => setMessagesError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoadingMessages(false))
  }, [quoteId])

  useEffect(() => {
    if (!quoteId) return
    setLoadingPositions(true)
    fetchQuotePositions(quoteId)
      .then(setPositions)
      .catch((err) => setPositionsError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoadingPositions(false))
  }, [quoteId])

  // Auto-Scroll: waehrend der User aktiv sendet immer scrollen (er will den
  // Verlauf verfolgen). Nur wenn er vorher hochgescrollt hat UND gerade kein
  // Send laeuft, wird das Banner statt Scroll gezeigt.
  const prevMessageCountRef = useRef(0)
  useEffect(() => {
    const added = messages.length - prevMessageCountRef.current
    prevMessageCountRef.current = messages.length
    if (added <= 0) return
    if (atBottom || sending) {
      scrollToBottom()
    } else {
      setUnseenCount((n) => n + added)
    }
  }, [messages, atBottom, sending, scrollToBottom])

  useEffect(() => {
    if (streamingText && (atBottom || sending)) scrollToBottom()
  }, [streamingText, atBottom, sending, scrollToBottom])

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, 5)
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        const base64 = dataUrl.split(',')[1] ?? ''
        if (!base64) return
        setAttachedImages((prev) =>
          prev.length < 5 ? [...prev, { mediaType: file.type, data: base64, preview: dataUrl }] : prev,
        )
      }
      reader.readAsDataURL(file)
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData.items)
    const imageItems = items.filter((item) => item.type.startsWith('image/'))
    if (imageItems.length === 0) return
    e.preventDefault()
    imageItems.forEach((item) => {
      const file = item.getAsFile()
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        const base64 = dataUrl.split(',')[1] ?? ''
        if (!base64) return
        setAttachedImages((prev) =>
          prev.length < 5 ? [...prev, { mediaType: file.type, data: base64, preview: dataUrl }] : prev,
        )
      }
      reader.readAsDataURL(file)
    })
  }

  async function handleSend(contentOverride?: string) {
    if (!quoteId) return
    const content = (contentOverride ?? input).trim()
    const imagesToSend = attachedImages.slice()
    if (!content && imagesToSend.length === 0) return

    // Falls noch ein Stream laeuft: erst abbrechen, dann unsere Message als
    // naechste in die Queue legen — der finally-Block des laufenden Streams
    // wird dann den pending-Send aufgreifen und neu starten.
    if (sending) {
      pendingSendRef.current = content
      setInput('')
      abortRef.current?.abort()
      return
    }

    setInput('')
    setAttachedImages([])
    setSendError(null)
    setSending(true)
    setStreamingText('')
    setStreamingTools([])

    // Optimistic user message (wird vom Server-Event ersetzt)
    const optimisticId = `optimistic-${Date.now()}`
    const optimisticUser: QuoteMessage = {
      id: optimisticId,
      quoteId,
      role: 'user',
      content,
      toolName: null,
      toolInputJson: null,
      toolOutputJson: null,
      imagesJson: imagesToSend.length > 0 ? imagesToSend.map((img) => ({ mediaType: img.mediaType, data: img.data })) : null,
      createdByUserId: currentUser?.id ?? null,
      createdByUserName: currentUser?.name ?? null,
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, optimisticUser])

    const abort = new AbortController()
    abortRef.current = abort
    let liveText = ''
    let positionsMaybeChanged = false
    let wasAborted = false

    try {
      await streamQuoteMessage(
        quoteId,
        content,
        (evt) => {
          if (evt.type === 'user-message') {
            setMessages((prev) => {
              const optimistic = prev.find((m) => m.id === optimisticId)
              const msg = { ...evt.message, createdByUserName: evt.message.createdByUserName ?? optimistic?.createdByUserName ?? null }
              return [...prev.filter((m) => m.id !== optimisticId), msg]
            })
          } else if (evt.type === 'text-delta') {
            liveText += evt.text
            setStreamingText(liveText)
          } else if (evt.type === 'tool-use-start') {
            // Neuer Tool-Call streamt rein → Live-Bubble hinzufuegen.
            setStreamingTools((prev) => [
              ...prev.filter((t) => t.index !== evt.index),
              { index: evt.index, toolName: evt.toolName, accumulatedJson: '' },
            ])
          } else if (evt.type === 'tool-input-delta') {
            setStreamingTools((prev) => prev.map((t) =>
              t.index === evt.index ? { ...t, accumulatedJson: evt.accumulatedJson } : t,
            ))
          } else if (evt.type === 'tool-persisted') {
            setMessages((prev) => [...prev, evt.message])
            // Persistierter Tool-Call ueberschreibt die Live-Bubble.
            setStreamingTools((prev) => prev.filter((t) => t.toolName !== evt.message.toolName))
            if (['add_position', 'update_position', 'remove_position'].includes(evt.message.toolName ?? '')) {
              positionsMaybeChanged = true
              // Sidebar sofort live mit-aktualisieren, statt erst am Ende
              // des Streams einmal zu refetchen — die KI legt waehrend eines
              // Turns oft mehrere Positionen an, der User soll das sehen.
              refetchPositions()
            }
            if (evt.message.toolName === 'update_quote_title') {
              // Refresh quote so updated title appears in page header immediately.
              refetchQuote()
            }
          } else if (evt.type === 'assistant-persisted') {
            setMessages((prev) => [...prev, evt.message])
            liveText = ''
            setStreamingText('')
            // Alle Live-Tool-Bubbles entfernen — der Turn ist durch, was
            // ausgefuehrt wurde, hat schon ein tool-persisted bekommen.
            setStreamingTools([])
          } else if (evt.type === 'error') {
            setSendError(evt.message)
          }
        },
        abort.signal,
        imagesToSend.length > 0 ? imagesToSend.map((img) => ({ mediaType: img.mediaType, data: img.data })) : undefined,
      )
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        wasAborted = true
        // Wenn ein pending-Send wartet, ist Abbruch erwuenscht — kein Fehler zeigen.
        if (!pendingSendRef.current) {
          setSendError('Antwort abgebrochen')
        }
      } else {
        setSendError(err instanceof Error ? err.message : 'Nachricht konnte nicht gesendet werden')
      }
      // Optimistic-Message nur entfernen wenn user-message-Event nie kam
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
    } finally {
      setSending(false)
      setStreamingText('')
      setStreamingTools([])
      abortRef.current = null
      if (positionsMaybeChanged) refetchPositions()
      inputRef.current?.focus()

      // Queue verarbeiten: wenn der User waehrenddessen schon die naechste
      // Frage abgeschickt hat, jetzt direkt starten.
      const next = pendingSendRef.current
      if (next) {
        pendingSendRef.current = null
        // setTimeout, damit React den state-Reset (sending=false) erst rendert
        setTimeout(() => handleSend(next), 0)
      } else if (wasAborted) {
        // Plain abort ohne pending-Send: nichts weiter zu tun
      }
    }
  }

  function handleStop() {
    // Reines Stop ohne Queue
    pendingSendRef.current = null
    abortRef.current?.abort()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Waehrend Streaming: Enter darf Newline einfuegen (normales Textarea-
      // Verhalten), damit der User in Ruhe weitertippen kann. Erst wenn der
      // Stream durch ist, sendet Enter.
      if (sending) return
      e.preventDefault()
      handleSend()
    }
  }

  function handleExportClick() {
    // Always open the guided transfer wizard — it handles customer, missing
    // articles, and the final export step in one coherent flow.
    setShowTransferWizard(true)
  }

  function maybeRouteErrorsToResolveModal(errors: ExportValidationError[]): boolean {
    const positionErrors = errors.filter((e) => e.field.startsWith('position_'))
    if (positionErrors.length === 0 || positionErrors.length !== errors.length) return false
    const ids = Array.from(
      new Set(positionErrors.map((e) => e.field.replace(/^position_/, ''))),
    )
    if (ids.length === 0) return false
    setResolveTargetIds(ids)
    setResolveHints(positionErrors.map((e) => e.message))
    setShowResolveItems(true)
    return true
  }

  async function handleChangeCustomer(selection: CustomerSelection) {
    if (!quoteId) return
    setChangeCustomerLoading(true)
    setChangeCustomerError(null)
    try {
      const updated = await updateQuote(quoteId, {
        customerSapBpCode: selection.customerSapBpCode,
        customerTanssId: selection.customerTanssId,
        customerName: selection.customerName,
        ...(selection.tenantId != null ? { tenantId: selection.tenantId } : {}),
      })
      setQuote(updated)
      setShowChangeCustomer(false)
      toast({
        title: selection.customerName
          ? `Kunde geändert auf „${selection.customerName}"`
          : 'Kunde vom Angebot entfernt',
        tone: 'success',
      })
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data
      const msg =
        data?.error ?? (err instanceof Error ? err.message : 'Kunde konnte nicht geändert werden')
      setChangeCustomerError(msg)
    } finally {
      setChangeCustomerLoading(false)
    }
  }

  async function handleDelete() {
    if (!quoteId) return
    setDeleting(true)
    try {
      await deleteQuote(quoteId)
      toast({ title: 'Angebot gelöscht', tone: 'success' })
      props.onDeleted?.()
      if (embedded && props.onClose) {
        props.onClose()
      } else {
        navigate('/auftraege')
      }
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data
      const msg = data?.error ?? (err instanceof Error ? err.message : 'Angebot konnte nicht gelöscht werden')
      toast({ title: 'Löschen fehlgeschlagen', description: msg, tone: 'danger' })
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  async function handleCopyQuote() {
    if (!quoteId || copying) return
    setCopying(true)
    try {
      const copy = await copyQuote(quoteId)
      toast({
        title: 'Angebot kopiert',
        description: `Neuer Entwurf „${copy.title}" wurde angelegt.`,
        tone: 'success',
      })
      navigate(`/auftraege/angebot/${copy.id}`)
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data
      const msg =
        data?.error ?? (err instanceof Error ? err.message : 'Angebot konnte nicht kopiert werden')
      toast({ title: 'Kopieren fehlgeschlagen', description: msg, tone: 'danger' })
    } finally {
      setCopying(false)
    }
  }

  async function handleReopenQuote() {
    if (!quoteId || reopening) return
    setReopening(true)
    try {
      const updated = await reopenQuote(quoteId)
      setQuote(updated)
      toast({ title: 'Angebot erneut geöffnet', description: 'Du kannst es jetzt bearbeiten und erneut an SAP übergeben.', tone: 'success' })
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data
      const msg = data?.error ?? (err instanceof Error ? err.message : 'Angebot konnte nicht erneut geöffnet werden')
      toast({ title: 'Erneut öffnen fehlgeschlagen', description: msg, tone: 'danger' })
    } finally {
      setReopening(false)
      setShowReopenConfirm(false)
    }
  }

  const customerLocked = !!quote && (quote.status === 'exported' || quote.status === 'archived')

  async function handleExport() {
    if (!quoteId) return
    setExporting(true)
    setExportErrors(null)
    setExportErrorMessage(null)
    try {
      const result = await exportQuoteToSap(quoteId)
      const tenantLabel = result.tenant?.code
        ? ` (Mandant ${result.tenant.code}${result.tenant.name ? ` · ${result.tenant.name}` : ''})`
        : ''
      toast({
        title: result.updated
          ? `Angebot QU-${result.docNum} in SAP aktualisiert${tenantLabel}`
          : `Angebot QU-${result.docNum} in SAP angelegt${tenantLabel}`,
        tone: 'success',
      })
      // Quote neu laden — Status wechselt auf "exported" + SAP-DocNum/Entry
      // werden in den Page-Header eingeblendet. NICHT mehr automatisch
      // wegnavigieren — der User soll die Bestaetigung sehen und das SAP-
      // Angebot direkt aus dem Dialog oeffnen koennen.
      refetchQuote()
      setShowSapPreview(true)
    } catch (err) {
      if (err instanceof QuoteExportValidationError) {
        setShowExportConfirm(false)
        const routed = maybeRouteErrorsToResolveModal(err.errors)
        if (!routed) setExportErrors(err.errors)
      } else {
        setExportErrorMessage(err instanceof Error ? err.message : 'Unbekannter Fehler beim SAP-Export')
      }
    } finally {
      setExporting(false)
      setShowExportConfirm(false)
    }
  }

  const title = quote
    ? quote.customerName
      ? `${quote.customerName} — ${SCENARIO_LABELS[quote.scenario] ?? quote.scenario}`
      : SCENARIO_LABELS[quote.scenario] ?? 'Angebot'
    : quoteId
      ? `Entwurf ${quoteId.slice(0, 8)}…`
      : 'Neues Angebot'

  return (
    <div className={embedded ? 'flex flex-col h-full min-h-0' : 'flex flex-col h-[calc(100vh-4rem)]'}>
      {!embedded && (
      <div className="flex-shrink-0 px-4 pt-4 md:px-6">
        <PageHeader
          title={title}
          breadcrumbs={[
            { label: 'Aufträge & Angebote', to: '/auftraege' },
            { label: loadingQuote ? '…' : title },
          ]}
          actions={
            <div className="flex items-center gap-2">
              <ActiveTenantBadge quoteTenantId={quote?.tenantId} />
              {quote && <ScenarioBadge scenario={quote.scenario} />}
              {quote && <StatusBadge status={`quote_${quote.status}`} />}
              {quote && !customerLocked && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<UserCog className="w-4 h-4" />}
                  onClick={() => {
                    setChangeCustomerError(null)
                    setShowChangeCustomer(true)
                  }}
                >
                  Kunde ändern
                </Button>
              )}
              {quote && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Copy className="w-4 h-4" />}
                  onClick={handleCopyQuote}
                  disabled={copying}
                  loading={copying}
                >
                  Kopieren
                </Button>
              )}
              {quote && (quote.status === 'draft' || quote.status === 'ready') && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Trash2 className="w-4 h-4" />}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                >
                  Löschen
                </Button>
              )}
              {quote && quote.status !== 'exported' && quote.status !== 'archived' && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<PackagePlus className="w-4 h-4" />}
                    onClick={() => setShowArticleWizard(true)}
                    title="Artikel-Assistent: fehlende SAP-Artikel Schritt für Schritt anlegen"
                  >
                    Artikel anlegen
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={<Upload className="w-4 h-4" />}
                    onClick={handleExportClick}
                    disabled={exporting}
                    loading={exporting}
                  >
                    An SAP übergeben
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowQuoteStepDebug(true)}
                    title="API-Schritte einzeln und manuell ausführen, mit voller SAP-Antwort"
                  >
                    Step-Debug
                  </Button>
                </>
              )}
              {quote && quote.status === 'exported' && quote.sapDocNum && (
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<Upload className="w-4 h-4" />}
                  onClick={() => setShowSapPreview(true)}
                  title="SAP-Angebot anzeigen"
                >
                  In SAP übergeben — QU-{quote.sapDocNum}
                </Button>
              )}
              {quote && (quote.status === 'exported' || quote.status === 'archived') && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<RefreshCw className="w-4 h-4" />}
                  onClick={() => setShowReopenConfirm(true)}
                  disabled={reopening}
                  loading={reopening}
                >
                  Erneut bearbeiten
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<ArrowLeft className="w-4 h-4" />}
                onClick={() => navigate('/auftraege')}
              >
                Zurück
              </Button>
            </div>
          }
        />
      </div>
      )}

      {/* ── Positionen ohne SAP-Artikelcode aufloesen ─────────────────── */}
      {quoteId && (
        <ResolveMissingItemsModal
          open={showResolveItems}
          quoteId={quoteId}
          positions={positions}
          targetPositionIds={resolveTargetIds}
          hints={resolveHints}
          tenantId={quote?.tenantId}
          onClose={() => {
            setShowResolveItems(false)
            setResolveTargetIds(undefined)
            setResolveHints(undefined)
          }}
          onAllResolved={() => {
            setShowResolveItems(false)
            setResolveTargetIds(undefined)
            setResolveHints(undefined)
            refetchPositions()
          }}
          onPositionUpdated={(updated) => {
            setPositions((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
          }}
        />
      )}

      {/* ── Article wizard (step-by-step SAP article creation) ─ */}
      {quoteId && (
        <SapArticleCreationWizard
          open={showArticleWizard}
          quoteId={quoteId}
          positions={positions}
          tenantId={quote?.tenantId}
          onClose={() => setShowArticleWizard(false)}
          onAllResolved={() => {
            setShowArticleWizard(false)
            refetchPositions()
          }}
          onPositionUpdated={(updated) => {
            setPositions((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
          }}
        />
      )}

      {/* ── SAP transfer wizard (guided 4-step flow) ───────────────── */}
      {quote && (
        <SapTransferWizardModal
          open={showTransferWizard}
          quote={quote}
          positions={positions}
          onClose={() => setShowTransferWizard(false)}
          onExportSuccess={(result) => {
            const tenantLabel = result.tenant?.code
              ? ` (Mandant ${result.tenant.code}${result.tenant.name ? ` · ${result.tenant.name}` : ''})`
              : ''
            toast({
              title: `Angebot QU-${result.docNum} in SAP angelegt${tenantLabel}`,
              tone: 'success',
            })
            refetchQuote()
            setShowTransferWizard(false)
            setShowSapPreview(true)
          }}
          onPositionUpdated={(updated) => {
            setPositions((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
          }}
          onQuoteUpdated={(updated) => {
            setQuote(updated)
          }}
        />
      )}

      {/* ── SAP-Angebot-Vorschau ─────────────────────────────────────── */}
      {quote && quote.sapDocEntry && (
        <DocumentDetailModal
          open={showSapPreview}
          onOpenChange={(o) => { if (!o) setShowSapPreview(false) }}
          doc={{
            id: quote.id,
            tenantId: quote.tenantId,
            docType: 'quotation' as DocType,
            sapDocEntry: quote.sapDocEntry,
            sapDocNum: quote.sapDocNum ?? null,
            cardCode: quote.customerSapBpCode ?? null,
            cardName: quote.customerName ?? null,
            docDate: null,
            docDueDate: null,
            docTotal: null,
            docCurrency: null,
            docStatus: null,
            isClosed: false,
            isCancelled: false,
            comments: null,
            linesJson: null,
            fullDataJson: null,
            lastSyncedAt: null,
            createdAt: quote.createdAt,
            updatedAt: quote.updatedAt,
            source: 'sap',
          } satisfies CachedDocument}
          onConvert={() => {}}
        />
      )}

      {/* ── Step-by-Step-SAP-Uebergabe-Debugger ───────────────────────── */}
      {quoteId && (
        <QuoteStepDebugModal
          open={showQuoteStepDebug}
          quoteId={quoteId}
          onClose={() => setShowQuoteStepDebug(false)}
          onExportComplete={() => {
            refetchQuote()
          }}
        />
      )}

      {/* ── Kunde nachtraeglich aendern ────────────────────────────────── */}
      <ChangeCustomerModal
        open={showChangeCustomer}
        onOpenChange={(open) => {
          setShowChangeCustomer(open)
          if (!open) setChangeCustomerError(null)
        }}
        title="Kunde des Angebots ändern"
        description="Wähle einen anderen Kunden für dieses Angebot. Hinweis: SAP-Übergabe ist nur mit SAP-Geschäftspartner möglich."
        confirmLabel="Kunde übernehmen"
        loading={changeCustomerLoading}
        errorMessage={changeCustomerError}
        initialCustomerName={quote?.customerName ?? null}
        allowEmpty
        tenantId={quote?.tenantId}
        onConfirm={handleChangeCustomer}
      />

      {/* ── Delete draft confirmation dialog ─────────────────────── */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Angebot löschen"
        message={`Möchtest du den Entwurf „${quote?.title ?? '…'}" wirklich löschen? Alle Positionen und der Chat-Verlauf werden unwiederbringlich entfernt.`}
        confirmLabel="Jetzt löschen"
        cancelLabel="Abbrechen"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* ── Reopen confirmation dialog ───────────────────────────────── */}
      <ConfirmDialog
        open={showReopenConfirm}
        title="Angebot erneut bearbeiten"
        message={`Das Angebot wird in den Entwurfs-Status zurückversetzt${quote?.sapDocNum ? ` und SAP-Nummer QU-${quote.sapDocNum} wird in die Historie verschoben` : ''}. Du kannst es anschließend bearbeiten und erneut an SAP übergeben.`}
        confirmLabel="Erneut öffnen"
        cancelLabel="Abbrechen"
        variant="default"
        loading={reopening}
        onConfirm={handleReopenQuote}
        onCancel={() => setShowReopenConfirm(false)}
      />

      {/* ── SAP export confirmation dialog ───────────────────────────── */}
      <ConfirmDialog
        open={showExportConfirm}
        title="An SAP übergeben"
        message={(() => {
          const tenant = quote?.tenantId ? tenants.find((t) => t.id === quote.tenantId) : null
          const mandantLabel = tenant ? ` (Mandant: ${tenant.code}${tenant.name ? ` · ${tenant.name}` : ''})` : ''
          return `Möchtest du das Angebot „${quote?.title ?? '…'}" jetzt als SAP-Quotation anlegen${mandantLabel}? Dieser Vorgang kann nicht rückgängig gemacht werden.`
        })()}
        confirmLabel="Jetzt übergeben"
        cancelLabel="Abbrechen"
        variant="default"
        loading={exporting}
        onConfirm={handleExport}
        onCancel={() => setShowExportConfirm(false)}
      />

      {/* ── Validierungs-/Fehler-Modal ────────────────────────────────── */}
      <Modal
        open={exportErrors !== null || exportErrorMessage !== null}
        onOpenChange={(open) => { if (!open) { setExportErrors(null); setExportErrorMessage(null) } }}
        title={exportErrorMessage ? 'Export fehlgeschlagen' : 'SAP-Übergabe noch nicht möglich'}
        footer={
          <Button
            variant="secondary"
            onClick={() => { setExportErrors(null); setExportErrorMessage(null) }}
          >
            Schließen
          </Button>
        }
      >
        <div className="px-5 py-4 space-y-3">
          {exportErrorMessage && (
            <p className="text-body text-red-700">{exportErrorMessage}</p>
          )}
          {exportErrors && exportErrors.length > 0 && (
            <div>
              <p className="text-body text-gray-700 mb-2">
                Bitte behebe folgende Probleme vor dem Export:
              </p>
              <ul className="space-y-1">
                {exportErrors.map((e, i) => (
                  <li key={i} className="flex gap-2 text-small text-red-700">
                    <span className="mt-0.5 flex-shrink-0">•</span>
                    <span>{e.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>

      {/* Embedded-Modus: die Aktions-Buttons werden via Portal in die
          Title-Leiste des umschliessenden Modals gerendert (Slot-Div mit der
          ID "quote-header-actions-slot" in DocumentDetailModal), damit alles
          auf einer Hoehe liegt. */}
      {embedded && quote && <EmbeddedHeaderActions
        quote={quote}
        customerLocked={customerLocked}
        exporting={exporting}
        deleting={deleting}
        reopening={reopening}
        onOpenChangeCustomer={() => {
          setChangeCustomerError(null)
          setShowChangeCustomer(true)
        }}
        onOpenStepDebug={() => setShowQuoteStepDebug(true)}
        onExportClick={handleExportClick}
        onOpenSapPreview={() => setShowSapPreview(true)}
        onDelete={() => setShowDeleteConfirm(true)}
        onShowReopenConfirm={() => setShowReopenConfirm(true)}
      />}

      {loadingQuote ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingState label="Angebot wird geladen…" />
        </div>
      ) : quoteError ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <ErrorState title="Angebot konnte nicht geladen werden" description={quoteError} />
        </div>
      ) : (
        <>
          {(() => {
            if (!quote?.sapExportHistory) return null
            let history: SapExportHistoryEntry[] = []
            try { history = JSON.parse(quote.sapExportHistory) as SapExportHistoryEntry[] } catch { return null }
            if (history.length === 0) return null
            return (
              <div className="mx-4 mt-3 md:mx-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
                <History className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-small text-amber-800">
                  <span className="font-medium">Bereits an SAP übergeben: </span>
                  {history.map((e, i) => (
                    <span key={i}>{i > 0 && ' · '}QU-{e.docNum ?? '–'}{e.exportedAt ? ` (${new Date(e.exportedAt).toLocaleDateString('de-DE')})` : ''}</span>
                  ))}
                </div>
              </div>
            )
          })()}
          <div className="flex-1 flex gap-4 px-4 pb-4 md:px-6 md:pb-6 overflow-hidden min-h-0">
          {/* ── Chat column ──────────────────────────────────────── */}
          <div className="flex flex-col flex-1 min-w-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-card">
            {/* Message list */}
            <div className="relative flex-1 min-h-0">
              <div
                ref={messagesScrollRef}
                onScroll={handleScroll}
                className="absolute inset-0 overflow-y-auto p-4 space-y-4"
              >
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <LoadingState label="Verlauf wird geladen…" />
                </div>
              ) : messagesError ? (
                <div className="flex items-center justify-center h-full">
                  <ErrorState title="Verlauf konnte nicht geladen werden" description={messagesError} />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <EmptyState
                    title="Noch keine Nachrichten"
                    description="Schreibe etwas, um den Dialog zu starten."
                  />
                </div>
              ) : (
                groupedMessages.map((item, idx) =>
                  'messages' in item ? (
                    <ToolGroupRow key={`tg-${idx}`} group={item} />
                  ) : (
                    <div key={item.id} className="flex flex-col gap-1">
                      {item.role === 'user' && Array.isArray(item.imagesJson) && item.imagesJson.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          {item.imagesJson.map((img, i) => (
                            <img
                              key={i}
                              src={`data:${img.mediaType};base64,${img.data}`}
                              alt=""
                              className="h-20 w-auto max-w-[160px] rounded-lg object-cover border border-gray-200 shadow-sm"
                            />
                          ))}
                        </div>
                      )}
                      <ChatBubble
                        role={item.role}
                        content={item.content ?? ''}
                        senderName={
                          item.role === 'user'
                            ? (item.createdByUserName ?? 'Unbekannter Benutzer')
                            : item.role === 'assistant'
                              ? 'KI-Assistent'
                              : undefined
                        }
                        avatarUrl={
                          item.role === 'user' && item.createdByUserId === currentUser?.id
                            ? (profilePhotoUrl ?? undefined)
                            : undefined
                        }
                        toolName={item.toolName ?? undefined}
                        toolInput={item.toolInputJson ?? undefined}
                        toolOutput={item.toolOutputJson ?? undefined}
                        timestamp={formatTime(item.createdAt)}
                      />
                    </div>
                  )
                )
              )}

              {/*
               * Live-Streaming-Bubble: zeigt den live wachsenden Text waehrend
               * Anthropic generiert. Sobald 'assistant-persisted' kommt, wird
               * streamingText geleert und die persistierte Message angehaengt.
               */}
              {sending && streamingText && (
                <ChatBubble
                  role="assistant"
                  content={streamingText}
                  senderName="KI-Assistent"
                  timestamp={formatTime(new Date().toISOString())}
                />
              )}

              {/* Live-Tool-Bubble: nur den aktuell laufenden Tool-Call anzeigen */}
              {sending && streamingTools.length > 0 && (
                <div className="flex gap-3 w-full">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center">
                    <Wrench className="w-4 h-4 animate-pulse" />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-caption font-semibold text-amber-800 font-mono animate-pulse">
                    {streamingTools[streamingTools.length - 1].toolName ?? 'tool'}…
                  </div>
                </div>
              )}

              {/* Spinner: nur wenn wir warten OHNE schon Text-Output zu haben */}
              {sending && !streamingText && (
                <div className="flex gap-3 items-center">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                    <Wrench className="w-4 h-4 text-amber-600 animate-pulse" />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl rounded-tl-none px-4 py-3 text-small text-amber-700 animate-pulse">
                    KI denkt nach…
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
              </div>

              {/* Sticky-Banner: zeigt sich, wenn neue Nachrichten kamen waehrend
                  der User nicht am Ende war. Klick scrollt nach unten. */}
              {!atBottom && unseenCount > 0 && (
                <button
                  type="button"
                  onClick={() => scrollToBottom({ force: true })}
                  className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-3 py-1.5 text-caption font-medium text-white shadow-lg hover:bg-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  {unseenCount} neue Nachricht{unseenCount === 1 ? '' : 'en'} ↓
                </button>
              )}
            </div>

            {/* Send error */}
            {sendError && (
              <div className="px-4 py-2 bg-red-50 border-t border-red-200">
                <p className="text-small text-red-700">{sendError}</p>
              </div>
            )}

            {/* Input bar */}
            <div className="flex-shrink-0 border-t border-gray-200 p-3 space-y-2">
              {/* Image preview strip */}
              {attachedImages.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {attachedImages.map((img, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={img.preview}
                        alt=""
                        className="h-14 w-auto max-w-[112px] rounded-lg object-cover border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => setAttachedImages((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Bild entfernen"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending || attachedImages.length >= 5}
                  className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Bild anhängen"
                  aria-label="Bild anhängen"
                >
                  <ImagePlus className="w-5 h-5" />
                </button>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value)
                    // Auto-resize: shrink first so scrollHeight reflects actual content
                    e.target.style.height = 'auto'
                    e.target.style.height = `${e.target.scrollHeight}px`
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder={
                    sending
                      ? 'Tippen während die KI antwortet — wird nach Abschluss gesendet…'
                      : 'Nachricht eingeben… (Enter senden, Shift+Enter Zeilenumbruch, Strg+V Bild einfügen)'
                  }
                  rows={1}
                  className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 max-h-36 overflow-auto"
                  style={{ minHeight: '2.5rem' }}
                />
                {sending ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleStop}
                    aria-label="Antwort abbrechen"
                    leadingIcon={<Square className="w-4 h-4" />}
                  >
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleSend()}
                    disabled={!input.trim() && attachedImages.length === 0}
                    aria-label="Senden"
                    leadingIcon={<SendHorizonal className="w-4 h-4" />}
                  >
                    Senden
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* ── Sidebar ──────────────────────────────────────────── */}
          <div className="hidden lg:flex flex-col w-80 xl:w-96 flex-shrink-0 bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
            {quote && (
              <QuoteHeaderPanel
                quote={quote}
                locked={customerLocked}
                onUpdate={handleQuoteHeaderUpdate}
              />
            )}
            {quoteId && (
              <QuoteSidebar
                quoteId={quoteId}
                positions={positions}
                loading={loadingPositions}
                error={positionsError}
                onRefetch={refetchPositions}
              />
            )}
          </div>
        </div>
        </>
      )}
    </div>
  )
}

// ─── Embedded-Modus: Aktions-Buttons in die Title-Leiste des Modals portalen ─

interface EmbeddedHeaderActionsProps {
  quote: { status: string; sapDocNum: number | null }
  customerLocked: boolean
  exporting: boolean
  deleting: boolean
  reopening: boolean
  onOpenChangeCustomer: () => void
  onOpenStepDebug: () => void
  onExportClick: () => void
  onOpenSapPreview: () => void
  onDelete: () => void
  onShowReopenConfirm: () => void
}

export function EmbeddedHeaderActions({
  quote,
  customerLocked,
  exporting,
  deleting,
  reopening,
  onOpenChangeCustomer,
  onOpenStepDebug,
  onExportClick,
  onOpenSapPreview,
  onDelete,
  onShowReopenConfirm,
}: EmbeddedHeaderActionsProps) {
  const [slot, setSlot] = useState<HTMLElement | null>(null)

  // Der Ziel-Slot wird vom DocumentDetailModal-Titel gerendert. Der Lookup
  // kann beim ersten Render noch leer sein (Modal mountet parallel) — daher
  // ein kleines Retry-Loop bis das Element existiert.
  useEffect(() => {
    if (slot) return
    let cancelled = false
    function tryFind() {
      if (cancelled) return
      const el = document.getElementById('quote-header-actions-slot')
      if (el) setSlot(el)
      else requestAnimationFrame(tryFind)
    }
    tryFind()
    return () => { cancelled = true }
  }, [slot])

  if (!slot) return null

  return createPortal(
    <>
      {(quote.status === 'draft' || quote.status === 'ready') && (
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Trash2 className="w-4 h-4" />}
          onClick={onDelete}
          disabled={deleting}
        >
          Löschen
        </Button>
      )}
      {!customerLocked && (
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<UserCog className="w-4 h-4" />}
          onClick={onOpenChangeCustomer}
        >
          Kunde ändern
        </Button>
      )}
      {quote.status !== 'exported' && quote.status !== 'archived' && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenStepDebug}
            title="API-Schritte einzeln und manuell ausführen, mit voller SAP-Antwort"
          >
            Step-Debug
          </Button>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Upload className="w-4 h-4" />}
            onClick={onExportClick}
            disabled={exporting}
            loading={exporting}
          >
            An SAP übergeben
          </Button>
        </>
      )}
      {quote.status === 'exported' && quote.sapDocNum && (
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<Upload className="w-4 h-4" />}
          onClick={onOpenSapPreview}
          title="SAP-Angebot anzeigen"
        >
          QU-{quote.sapDocNum} in SAP öffnen
        </Button>
      )}
      {(quote.status === 'exported' || quote.status === 'archived') && (
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<RefreshCw className="w-4 h-4" />}
          onClick={onShowReopenConfirm}
          disabled={reopening}
          loading={reopening}
        >
          Erneut bearbeiten
        </Button>
      )}
    </>,
    slot,
  )
}
